import { getCookie } from '@utiljs/cookie';
import { getHash } from '@utiljs/param';
import Horn from '@utils/horn'; // eslint-disable-line import/no-cycle
import { isHitGray, ICommoneHornGrayConfig } from '@utils/commonHornGrayUtils'; // eslint-disable-line import/no-cycle

const defaultTags = {
    cityid: getCookie('cityId'),
    acctId: getCookie('acctId'),
    wmPoiId: getCookie('wmPoiId'),
};

type ErrorInfo = {
    /** 错误名称(二级分类) */
    name: string;
    /** 错误内容 */
    message: string;
    /** 一级分类，默认为 “jsError” */
    category?: string;
    /** 其它扩展信息对象 */
    tags?: { [index: string]: string | number | boolean };
    /** 错误级别，默认 error */
    level?: 'ERROR' | 'WARN' | 'INFO';
};

/**
 * 上报自定义Raptor/Merlin错误
 */
const reportCustomError = ({
    name,
    message,
    category = 'jsError',
    tags = {},
    level = 'ERROR',
}: ErrorInfo) => {
    try {
        if (window.Owl) {
            Owl.addError(
                {
                    name,
                    msg: message,
                },
                {
                    level: Owl.errorModel.LEVEL[level],
                    combo: false,
                    tags: { ...defaultTags, ...tags },
                    category,
                },
            );
        }
    } catch (e) {
        console.log('自定义 warn 上报失败：', e);
    }
};

interface IPageInfo {
    name: string;
}

/**
 * 上报页面白屏错误
 *
 * @param error 错误信息
 * @param ErrorInfo 错误堆栈信息
 * @param pageInfo 页面信息
 */

const reportWhitePageError = (error: Error, ErrorInfo: React.ErrorInfo, pageInfo?: IPageInfo) => {
    reportCustomError({
        name: `${pageInfo?.name || getHash(window.location.href)}`,
        category: 'WHITE_PAGE_ERROR',
        message: `页面信息：${JSON.stringify(pageInfo)}
                    错误信息：${error && error.message}；
                    错误堆栈：${error && error.stack}；
                    组件堆栈：${ErrorInfo && ErrorInfo.componentStack}。`,
    });
};

/**
 * [corefe Raptor自定义指标配置](https://raptor.mws.sankuai.com/frontend/settings/config/custom-target?id=8874)
 */
const CUSTOM_METRICS = {
    /** epassport全部加载次数(整体加载算一次) */
    EP_LOAD_ALL: {
        KEY: 'EP_LOAD_ALL',
    },
    /** epassport模块加载成功 */
    EP_LOAD_SUCCESS: {
        KEY: 'EP_LOAD_SUCCESS',
        TAGS: {
            /** 取值：epassport/waimaie/eepassport */
            DOMAIN: 'domain',
        },
    },
    /** epassport模块加载失败 */
    EP_LOAD_FAIL: {
        KEY: 'EP_LOAD_FAIL',
        TAGS: {
            /** 取值：epassport/waimaie/eepassport */
            DOMAIN: 'domain',
        },
    },
    /** 登录核心接口 */
    LOGON_API: {
        KEY: 'logon_API',
        TAGS: {
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
            /** 账号类型 */
            ACCT_TYPE: 'acct_type',
        },
    },
    /** 基础信息HomePage接口调用 */
    HOMEPAGE_API: {
        KEY: 'HOMEPAGE_API',
        TAGS: {
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
        },
    },
    /** 菜单信息Querymenu接口调用 */
    QUERYMENU_API: {
        KEY: 'QUERYMENU_API',
        TAGS: {
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
        },
    },
    /** 全部打印请求 */
    PRINT_REQUEST_ALL: {
        KEY: 'PRINT_REQUEST_ALL',
        TAGS: {
            /** 订单业务类型：phf/wm */
            BUSINESS_TYPE: 'businessType',
            IS_UNIFIED: 'is_unified',
        },
    },
    /** 成功完成打印 */
    PRINT_SUCCESS: {
        KEY: 'PRINT_SUCCESS',
        TAGS: {
            /** 订单业务类型：phf/wm */
            BUSINESS_TYPE: 'businessType',
            /** 接单容器：clientV4, clientV5, web */
            RUNTIME: 'RUNTIME',
            /** 是否 XP */
            IS_XP: 'isXP',
            IS_UNIFIED: 'is_unified',
        },
    },
    /** 打印失败 */
    PRINT_FAIL: {
        KEY: 'PRINT_FAIL',
        TAGS: {
            /** 订单业务类型：phf/wm */
            BUSINESS_TYPE: 'businessType',
            /** 失败原因编码 */
            ERROR_CODE: 'errorCode',
            /** 小票模板ID */
            TEMPLATE_ID: 'templateId',
            /** 订单所属门店ID */
            ORDER_POI_ID: 'orderPoiId',
            /** 订单展示ID */
            ORDER_VIEW_ID: 'orderViewId',
            IS_UNIFIED: 'is_unified',
        },
        ERROR_CODE: {
            /** 未取得订单打印详情数据（接口请求失败 or 接口没有返回数据） */
            ORDER_DETAIL_FETCH_FAIL: 'orderDetailFetchFail',
            /** 未找到可用的打印机 */
            PRINTER_LIST_EMPTY: 'printerListEmpty',
            /** 构造打印数据失败 */
            COMPOSE_PRINT_DETAIL_FAIL: 'composePrintDetailFail',
            /** 小票模板未加载成功 */
            PRINT_TEMPLATE_NOT_AVAILABLE: 'printTemplateNotAvailable',
            /** 小票模板render失败 */
            RENDER_PRINT_TEMPLATE_FAIL: 'renderPrintTemplateFail',
            /** 调用 wmPrinterApi 报错 */
            WM_PRINTER_API_FAIL: 'wmPrinterApiFail',
        },
    },
    /** 小票打印广告API */
    PRINT_AD_API: {
        KEY: 'PRINT_AD_API',
        TAGS: {
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
        },
    },
    /** 小票打印模板配置API */
    PRINT_TEMPLATE_CONFIG_API: {
        KEY: 'PRINT_TEMPLATE_CONFIG_API',
        TAGS: {
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
        },
    },
    /** 小票打印打印详情API */
    PRINT_ORDER_DETAIL_API: {
        KEY: 'PRINT_ORDER_DETAIL_API',
        TAGS: {
            /** 订单业务类型：phf/wm */
            BUSINESS_TYPE: 'businessType',
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
            IS_UNIFIED: 'is_unified',
        },
    },
    /** 小票打印打印详情API（目前仅PHF拼单一起送使用) */
    PRINT_ORDER_DETAIL_API_PHF: {
        KEY: 'PRINT_ORDER_DETAIL_API_PHF',
        TAGS: {
            /** 订单业务类型：phf/wm */
            BUSINESS_TYPE: 'businessType',
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
            IS_UNIFIED: 'is_unified',
        },
    },
    /** 运行时长任务自定义上报 */
    LONG_TASK: {
        KEY: 'longTask',
        TAGS: {
            /** 发生长任务的框架 */
            IFRAME_NAME: 'iframeName',
        },
        EXTRA: {
            ACCT_ID: 'acctId',
            IFRAME_URL: 'iframeUrl',
        },
    },
    /** 新订单详情轮询接口 */
    ORDER_OFQ_API: {
        KEY: 'ORDER_OFQ_API',
        TAGS: {
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
            IS_UNIFIED: 'is_unified',
            /**
             * 启动器标识，用以区分是否命中轮询改造
             * - pull_channel：旧的轮询通道
             * - message_hub：消息中心（的自定义消息源）
             */
            INITIATOR: 'initiator',
        },
    },
    /** 新订单push拉订单详情接口 */
    // eslint-disable-next-line @typescript-eslint/camelcase
    ORDER_orderFromPush_API: {
        KEY: 'ORDER_orderFromPush_API',
        TAGS: {
            /** 订单业务类型：phf/wm */
            BUSINESS_TYPE: 'businessType',
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
            /** 是否命中履约2.0 */
            IS_ORDER2: 'is_order2',
            /** 是否自动接单 */
            IS_AUTO_ACCEPT: 'is_auto_accept',
            /**
             * Push 启动器标识，用以区分是否命中新订单 Push 改造
             * - push_channel：旧的 Push 通道
             * - message_hub：消息中心（的 Push 消息源）
             */
            INITIATOR: 'initiator',
            IS_UNIFIED: 'is_unified',
        },
    },
    /** 轮询到的新订单 */
    ORDER_FROM_POLLER: {
        KEY: 'ORDER_FROM_POLLER',
        TAGS: {
            /** 订单业务类型 */
            BUSINESS_TYPE: 'business_type',
            /** 是否是订单索引模型新 VO */
            IS_INDEX_VO: 'is_index_vo',
            /** 启动器标识 */
            INITIATOR: 'initiator',
            IS_UNIFIED: 'is_unified',
        },
    },
    PHFWITHWM_CONFIRM: {
        KEY: 'PHFWITHWM_CONFIRM',
        TAGS: {
            /** 订单业务类型：phf/wm */
            BUSINESS_TYPE: 'businessType',
        },
    },
    /** 确认订单 */
    CONFIRM_ORDER: {
        KEY: 'CONFIRM_ORDER',
        TAGS: {
            /** 订单业务类型：phf/wm */
            BUSINESS_TYPE: 'businessType',
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
            /** 接单类型 manual/auto_push/auto_loop */
            OPERATION_TYPE: 'operationType',
            /** 是否命中履约2.0 */
            IS_ORDER2: 'is_order2',
            /** 接单容器：clientV4, clientV5, web */
            RUNTIME: 'RUNTIME',
            /** 是否 xp */
            IS_XP: 'isXP',
            IS_UNIFIED: 'is_unified',
        },
    },
    /** 配送日志API */
    LOGISTICS_LOG_API: {
        KEY: 'LOGISTICS_LOG_API',
        TAGS: {
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
        },
    },
    /** 点击取消订单按钮 */
    CLICK_CANCEL_ORDER_BUTTON: {
        KEY: 'CLICK_CANCEL_ORDER_BUTTON',
    },
    /** 展示“取消订单原因选择”弹框 */
    SHOW_CHOOSE_CANCEL_ORDER_REASON_MODAL: {
        KEY: 'SHOW_CHOOSE_CANCEL_ORDER_REASON_MODAL',
    },
    /** 点击确认取消订单按钮 */
    CLICK_CONFIRM_CANCEL_ORDER_BUTTON: {
        KEY: 'CLICK_CONFIRM_CANCEL_ORDER_BUTTON',
        TAGS: {
            IS_ORDER2: 'is_order2',
        },
    },
    /** 新版EP SDK PV */
    EP_NEW_SDK_PV: {
        KEY: 'EP_NEW_SDK_PV',
    },
    /** 新版 EP SDK 登录成功回调 */
    EP_NEW_SDK_LOGIN_SUCCESS: {
        KEY: 'EP_NEW_SDK_LOGIN_SUCCESS',
        TAGS: {
            LOGIN_TYPE: 'loginType',
        },
    },
    /** 打印云小票模板API */
    PRINT_TEMPLATE_API: {
        KEY: 'PRINT_TEMPLATE_API',
        TAGS: {
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
            /** 账号ID */
            ACCT_ID: 'acct_id',
            /** 此次请求入参中门店数量 */
            POI_COUNT: 'poi_count',
        },
    },
    /** URL替换命中 */
    URL_REPLACE_HIT: {
        KEY: 'URL_REPLACE_HIT',
        TAGS: {
            SRC: 'src',
            DEST: 'dest',
            CURRENT_HASH: 'currentHash',
        },
    },
    ORDER_UNPROCESS_LIST_API: {
        KEY: 'ORDER_UNPROCESS_LIST_API',
        TAGS: {
            /** HTTP 状态码是否成功，取值 1成功/0失败 */
            HTTP_SUCCESS: 'http_success',
            /** 业务状态码，如实上报 */
            BUSINESS_CODE: 'business_code',
            /** 是否命中履约2.0 */
            IS_ORDER2: 'is_order2',
            /** 账号ID */
            ACCT_ID: 'acct_id',
            /** 门店ID */
            POI_ID: 'poi_id',
        },
    },
    /** Push 连接建立 */
    PUSH_CONNECTION: {
        KEY: 'PUSH_CONNECTION',
        TAGS: {
            /** 本次连接是否成功 */
            IS_SUCCESS: 'is_success',
            /**
             * 如果本次连接成功，则为第几次连接成功；
             * 如果本次连接失败，则为截至目前成功连接的次数。
             * （因为连接可能因为各种原因断开，而 PushClient 有重连机制，因此可能有多次连接成功的记录）
             */
            OPEN_COUNT: 'open_count',
            /**
             * 如果本次连接成功，则为本次连接成功的尝试次数；
             * 如果本次连接失败，则为距离上一次连接成功的尝试次数。
             */
            TRY_COUNT: 'try_count',
            /**
             * 启动器标识，用以区分是否命中 Push 改造
             * - push_channel：旧的 Push 通道
             * - message_hub：消息中心（的 Push 消息源）
             */
            INITIATOR: 'initiator',
        },
    },
    /** Push 连接断开 */
    PUSH_DISCONNECTION: {
        KEY: 'PUSH_DISCONNECTION',
        TAGS: {
            /**
             * 本次断开的连接是第几次
             */
            OPEN_COUNT: 'open_count',
            /**
             * 启动器标识，用以区分是否命中 Push 改造
             * - push_channel：旧的 Push 通道
             * - message_hub：消息中心（的 Push 消息源）
             */
            INITIATOR: 'initiator',
        },
    },
    /** 4.x 客户端强制升级引导 - 强制踢蹬 */
    CLIENTV4_FORCE_UPGRADE_LOGOUT: {
        KEY: 'CLIENTV4_FORCE_UPGRADE_LOGOUT',
        TAGS: {
            DEVICE_UUID: 'device_uuid',
            ACCT_ID: 'acct_id',
        },
    },
    /** 4.x 客户端强制升级引导 - 倒计时结束后弹框 */
    CLIENTV4_FORCE_UPGRADE_COUNTDOWN_DONE_DIALOG: {
        KEY: 'CLIENTV4_FORCE_UPGRADE_COUNTDOWN_DONE_DIALOG',
        TAGS: {
            DEVICE_UUID: 'device_uuid',
        },
    },
    /** 4.x 客户端强制升级引导 - 倒计时中弹框 */
    CLIENTV4_FORCE_UPGRADE_COUNTDOWN_DIALOG: {
        KEY: 'CLIENTV4_FORCE_UPGRADE_COUNTDOWN_DIALOG',
        TAGS: {
            DEVICE_UUID: 'device_uuid',
            /** 展示完此次弹框后，还剩几天 */
            LEFT_DAY: 'left_day',
        },
    },
    /** 4.x 客户端强制升级引导 - 全部弹框展示 */
    CLIENTV4_FORCE_UPGRADE_DIALOG: {
        KEY: 'CLIENTV4_FORCE_UPGRADE_DIALOG',
        TAGS: {
            DEVICE_UUID: 'device_uuid',
            /** 展示完此次弹框后，还剩几天 */
            LEFT_DAY: 'left_day',
        },
    },
    /** 4.x 客户端强制升级引导 - 首页postmessage埋点 */
    CLIENTV5_FORCE_UPGRADE_INDEX_PAGE_MSG: {
        KEY: 'CLIENTV5_FORCE_UPGRADE_INDEX_PAGE_MSG',
        TAGS: {
            ACCT_ID: 'acct_id',
            /** 门店ID */
            POI_ID: 'poi_id',
        },
    },
    /** 4.x 客户端强制升级引导 - shell接收消息 */
    CLIENTV5_FORCE_UPGRADE_MSG_RECIEVED: {
        KEY: 'CLIENTV5_FORCE_UPGRADE_MSG_RECIEVED',
        TAGS: {
            ACCT_ID: 'acct_id',
            /** 门店ID */
            POI_ID: 'poi_id',
        },
    },
    /** 4.x 客户端强制升级引导 - shell请求强制升级弹窗内容 */
    CLIENTV5_FORCE_UPGRADE_REQUEST: {
        KEY: 'CLIENTV5_FORCE_UPGRADE_REQUEST',
        TAGS: {
            ACCT_ID: 'acct_id',
            /** 门店ID */
            POI_ID: 'poi_id',
            /** 弹窗类型 */
            TYPE: 'type',
        },
    },
    /** 账号类型判断失败 */
    ACCOUNT_JUMP_FAIL: {
        KEY: 'ACCOUNT_JUMP_FAIL',
        TAGS: {
            REASON: 'reason',
            ACCTID: 'acctId',
            SCINDEX: 'scIndex',
        },
    },
    /** 账号类型判断失败，踢蹬 */
    ACCOUNT_JUMP_FAIL_LOGOUT: {
        KEY: 'ACCOUNT_JUMP_FAIL_LOGOUT',
        TAGS: {
            ACCTID: 'acctId',
            SCINDEX: 'scIndex',
        },
    },
    /** 四轮弹窗车型列表请求次数 */
    FOUR_WHEEL_DELIVERY_REQCOUNT: {
        KEY: 'FOUR_WHEEL_DELIVERY_REQCOUNT',
        TAGS: {
            ENTRY: 'entry', //入口
            REQ_TYPE: 'req_type', //呼叫或者追加呼叫
        },
    },
    /** 四轮弹窗车型列表接口响应 */
    FOUR_WHEEL_DELIVERY_REQCOUNT_RES: {
        KEY: 'FOUR_WHEEL_DELIVERY_REQCOUNT_RES',
        TAGS: {
            BUSINESS_CODE: 'business_code', //返回的code码
            REQ_TYPE: 'req_type', //呼叫或者追加呼叫
        },
    },
    /** 四轮弹窗车型呼叫次数 */
    FOUR_WHEEL_DELIVERY_BUTTON_COUNT: {
        KEY: 'FOUR_WHEEL_DELIVERY_BUTTON_COUNT',
        TAGS: {
            ENTRY: 'entry', //入口
            REQ_TYPE: 'req_type', //呼叫或者追加呼叫
        },
    },
    /** 四轮弹窗车型呼叫结果 */
    FOUR_WHEEL_DELIVERY_BUTTON_COUNT_RES: {
        KEY: 'FOUR_WHEEL_DELIVERY_BUTTON_COUNT_RES',
        TAGS: {
            BUSINESS_CODE: 'business_code', //返回的code码
            REQ_TYPE: 'req_type', //呼叫或者追加呼叫
        },
    },
    /** 订单延迟自动接单问题排查 */
    ORDER_DELAY: {
        KEY: 'ORDER_DELAY',
        TAGS: {
            TYPE: 'type', // 'ifPoiEnableAutoAcceptLocally' | 'pushMessage'
        },
    },
    /** localStorage设置失败 */
    SET_LOCAL_STORAGE_FAIL: {
        KEY: 'SET_LOCAL_STORAGE_FAIL',
        TAGS: {
            TYPE: 'type', // 'mtdpmap' | 'amap'
            ACCTID: 'acctId',
        },
    },
};

/**
 * FIXME: 临时写死的需要强制走采样逻辑的自定义指标
 * 目前主页面重构还在灰度中，但由于自定义指标的采样配置是通过Horn下发的
 * 所以在旧版主页取不到采样配置的情况下不能简单的认为命中采样。
 *
 * 在以下数组中的指标只有明确读取到采样配置且符合采样规则的情况下才会上报
 */
const TMEP_SAMPLE_METRICS_KEY = [
    'PRINT_REQUEST_ALL',
    'PRINT_SUCCESS',
    'PRINT_FAIL',
    'PRINT_AD_API',
    'PRINT_TEMPLATE_CONFIG_API',
    'PRINT_ORDER_DETAIL_API',
    'PRINT_ORDER_DETAIL_API_PHF',
];

type MetricsGroupSampleConfig = {
    metricsKeyList: string[];
    sampleConfig: ICommoneHornGrayConfig;
};

/** 自定义指标是否命中采样规则 */
const isMetricsHitSampleConfig = (metricsKey: string): boolean => {
    if (metricsKey === CUSTOM_METRICS.ORDER_OFQ_API.KEY) {
        // 如果是新订单详情轮询接口，强制随机1%采样
        // why:
        // 首先，该订单请求量巨大（单日请求量 > 3亿，即使采样1%，单日也有300w+打点数据） https://raptor.mws.sankuai.com/application/transaction/type?reportType=day&date=&startDate=20220322000000&endDate=20220322235900&r=21916&ip=All&group=&domain=com.sankuai.waimaieapp.apigateway&query=ofq&fold=All&type=URL
        // 其次，为了保证可以采样到全部账号数据，没有采用惯常的账号尾号取样方式
        return Math.random() < 0.01;
    }

    if (TMEP_SAMPLE_METRICS_KEY.indexOf(metricsKey) === -1) {
        // 如果不在强制采样名单里，默认命中上报
        return true;
    } else {
        const configList = Horn.getConfigByFieldName<MetricsGroupSampleConfig[]>(
            Horn.CONFIG_FIELD.RAPTOR_METRICS_SAMPLE_CONFIG,
        );
        if (Array.isArray(configList)) {
            // 默认不上报，只要明确命中的规则才上报
            let result = false;

            try {
                configList.forEach(sampleConfig => {
                    if (
                        sampleConfig.metricsKeyList.indexOf(metricsKey) !== -1 &&
                        isHitGray(sampleConfig.sampleConfig)
                    ) {
                        result = true;
                    }
                });
            } catch (e) {
                if (window.Owl && window.Owl.addError) {
                    window.Owl.addError(e);
                    console.log(e);
                }
            }

            return result;
        } else {
            // 在强制采样名单，但是没有读取到采样配置，默认不上报
            return false;
        }
    }
};

/**
 * 上报自定义指标
 *
 * _📢  OWL SDK中上报自定义指标强制为延迟上报(默认1500ms)，本工程OWL初始化时已设置成 10ms。_
 *
 * _📢  如需在页面跳转前上报自定义指标，请务必给跳转操作增加延时，否则可能导致上报数据不完整。_
 *
 * 附[Owl手动上报自定义指标文档](https://km.sankuai.com/custom/onecloud/page/253147798#id-%E6%89%8B%E5%8A%A8%E4%B8%8A%E6%8A%A5%E8%87%AA%E5%AE%9A%E4%B9%89%E6%8C%87%E6%A0%87)
 *
 * @param metricsKey 指标名称（需提前在Raptor系统配置）
 * @param metricsValue 指标值
 * @param tags 扩展维度（需提前在Raptor系统配置）
 */
const reportCustomMetrics = (
    metricsKey: string,
    metricsValue: string | number,
    tags?: { [key: string]: string | number },
    ExtraData?: { [key: string]: string | number },
) => {
    try {
        if (isMetricsHitSampleConfig(metricsKey)) {
            if (window.Owl) {
                const metricsInstance = window.Owl.MetricManager();
                if (metricsInstance) {
                    if (tags) {
                        metricsInstance.setTags(tags);
                    }
                    if (ExtraData) {
                        metricsInstance.setExtraData(ExtraData);
                    }
                    metricsInstance.setMetric(metricsKey, metricsValue);
                }
            }
        }
    } catch (e) {
        console.log('自定义指标上报失败：', e);
    }
};

export { reportWhitePageError, reportCustomError, reportCustomMetrics, CUSTOM_METRICS };
